import { useEffect, useState } from 'react';

import { BackendRoute } from '../../../config/index';
import { HttpException } from '../../../exceptions/http-exception.util';
import { ApiService } from '../../../services/index';
import { SPACE_BACKEND_ROUTE } from '../constants/spaces-query.const';
import { PreviewedFile } from '../store/file-preview.store';
import { DataRoomViewer, useDataRoomViewer } from './use-recipient-view.hook';

export enum FilePreviewState {
  FAILED = 'failed',
  LOADING = 'loading',
  READY = 'ready',
  /** The server has nothing it can draw — the screen offers the download instead. */
  UNSUPPORTED = 'unsupported',
}

export type SpaceFilePreview = {
  /** The bytes, for a renderer that parses the file rather than pointing a tag at it. */
  blob: Blob | null;
  contentType: null | string;
  state: FilePreviewState;
  url: null | string;
};

const UNSUPPORTED_STATUS = 415;

/**
 * The bytes of one file as something the page can draw, and the state of getting them.
 *
 * Deliberately outside the query cache: what comes back is an object URL, which has to be revoked
 * when the viewer closes or moves on, and a cache that handed the same URL to a later reader would
 * hand them a revoked one. The file is small — 10 MB at most — so re-reading it on a second open
 * costs less than a cache that has to be invalidated by hand.
 *
 * Which route carries it is decided before the request, as everywhere else in the module: the
 * owner's module routes refuse a recipient, and a recipient's route is scoped by the server to the
 * room they were shared. Neither takes a company from the client.
 */
export const useSpaceFilePreview = (file: null | PreviewedFile): SpaceFilePreview => {
  const viewer = useDataRoomViewer();
  const [preview, setPreview] = useState<SpaceFilePreview>({
    blob: null,
    contentType: null,
    state: FilePreviewState.LOADING,
    url: null,
  });

  // The three things that identify the request, not the object carrying them: a caller that
  // builds `{ fileId, space }` inline would otherwise hand this a new object on every render, and
  // since the effect sets state, each run would cause the next one — a loop that reads as a
  // hanging tab. Primitives make the hook safe for any caller, not only the one with a store.
  const fileId = file?.fileId ?? null;
  const spaceId = file?.space.id ?? null;
  const spaceKind = file?.space.kind ?? null;

  useEffect(() => {
    if (!fileId || !spaceId || !spaceKind || viewer === DataRoomViewer.UNRESOLVED) return;

    let objectUrl: null | string = null;
    // A reader who closes the viewer before the bytes arrive must not be shown them.
    let isCurrent = true;

    setPreview({ blob: null, contentType: null, state: FilePreviewState.LOADING, url: null });

    void ApiService.fetchFilePreview({
      endpoint:
        viewer === DataRoomViewer.RECIPIENT
          ? BackendRoute.MY_DATA_ROOMS
          : SPACE_BACKEND_ROUTE[spaceKind],
      routePath: [spaceId, 'files', fileId, 'preview'],
    })
      .then(({ blob, contentType, url }) => {
        objectUrl = url;

        if (isCurrent) setPreview({ blob, contentType, state: FilePreviewState.READY, url });
      })
      .catch((error: unknown) => {
        if (!isCurrent) return;

        // 415 is the server saying it has no rendering for this file, which is a different answer
        // from a failure: one offers the download, the other offers to try again.
        const state =
          error instanceof HttpException && error.response.status === UNSUPPORTED_STATUS
            ? FilePreviewState.UNSUPPORTED
            : FilePreviewState.FAILED;

        setPreview({ blob: null, contentType: null, state, url: null });
      });

    return () => {
      isCurrent = false;

      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, spaceId, spaceKind, viewer]);

  return preview;
};
